import * as B from '@blueprintjs/core';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiShareLink, ApiShareLinkDeleteResponse} from 'app/modules/Remote/ShareLink';

const ShareLinkDeleteDialog: React.FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  shareLinks: ApiShareLink[];
  onDeleted: (result: ApiShareLinkDeleteResponse) => void;
  onError: (error: Error) => void;
}> = ({isOpen, onClose, organizationId, shareLinks, onDeleted, onError}) => {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await api.shareLinks(organizationId).batchDelete(ids);
      return response.get('data').toJS() as ApiShareLinkDeleteResponse;
    },
    onSuccess: onDeleted,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['shareLinks', organizationId]});
      onClose();
    },
  });

  // The selection is cleared on success while the dialog is still fading out,
  // so keep showing the links it was opened with.
  const shownLinksRef = React.useRef(shareLinks);
  if (isOpen) {
    shownLinksRef.current = shareLinks;
  }
  const count = shownLinksRef.current.length;

  return (
    <B.Alert
      isOpen={isOpen}
      cancelButtonText="Cancel"
      confirmButtonText="Delete"
      intent={B.Intent.DANGER}
      icon="trash"
      loading={deleteMutation.isPending}
      canOutsideClickCancel
      canEscapeKeyCancel
      onCancel={onClose}
      onConfirm={() => deleteMutation.mutate(shownLinksRef.current.map((link) => link.id))}
    >
      <p>
        Are you sure you want to delete{' '}
        {count === 1 ? 'this share link' : `these ${count} share links`}?
      </p>
      <p>
        Anyone who has the {count === 1 ? 'link' : 'links'} will no longer be able to open{' '}
        {count === 1 ? 'it' : 'them'}.
      </p>
    </B.Alert>
  );
};

export default ShareLinkDeleteDialog;
